import { Divider, Stack } from '@mui/material';
import { FilterParams, UserRole } from '@synergy/models';
import { FormattedNumber } from 'react-intl';
import {
    PoolAccordion,
    PoolAccordionItem,
    PoolLineClampTypography,
    PoolRow,
    PoolTypography,
} from '@synergy/components-next';
import { ReactElement } from 'react';
import { useSearchParams } from 'react-router-dom';

import { AccessControl } from '../../../components/access-control/access-control';
import { BestPriceFilter } from '../filter-toggles/best-price-filter/best-price-filter';
import { EcoProductFilter } from '../filter-toggles/eco-product-filter/eco-product-filter';
import { FilterAccordionDetails } from '../../../components/filter/styled-components/filter-accordion-details';
import { FilterAccordionSummary } from '../../../components/filter/styled-components/filter-accordion-summary';
import { FilterAttribute, FilterPrice, FilterSupplierSearchPage } from '../../../components/filter';
import { SpecialPriceFilter } from '../filter-toggles/special-price-filter/special-price-filter';
import { articleSearchResultsSelector, initialArticleSearchResultsSelector } from '../../../store/articles-selectors';
import { currencySelector, userSelector } from '../../../store/configuration-selectors';
import { useAppSelector } from '../../../../store/store';

interface SearchPageFiltersContentProps {
    isPriceOpen: boolean;
}

export const SearchPageFiltersContent = (props: SearchPageFiltersContentProps): ReactElement => {
    const { isPriceOpen = false } = props;
    const [searchParams] = useSearchParams();
    const user = useAppSelector(userSelector);
    const currency = useAppSelector(currencySelector);
    const articleSearchResults = useAppSelector(articleSearchResultsSelector);
    const initialArticleSearchResults = useAppSelector(initialArticleSearchResultsSelector);

    const isSpecialOffer = !!searchParams.get(FilterParams.SpecialOffer);
    const minPrice = searchParams.get(FilterParams.MinPrice);
    const maxPrice = searchParams.get(FilterParams.MaxPrice);
    const suppliers = searchParams.getAll(FilterParams.SupplierIds);

    const renderMinMaxPrice = (): ReactElement | null => {
        if (minPrice && maxPrice) {
            return (
                <>
                    <FormattedNumber value={Number(minPrice)} style="currency" currency={currency} />
                    {' - '}
                    <FormattedNumber value={Number(maxPrice)} style="currency" currency={currency} />
                </>
            );
        }

        if (minPrice && !maxPrice) {
            return <FormattedNumber value={Number(minPrice)} style="currency" currency={currency} />;
        }

        if (!minPrice && maxPrice) {
            return (
                <>
                    <FormattedNumber value={0} style="currency" currency={currency} />
                    {' - '}
                    <FormattedNumber value={Number(maxPrice)} style="currency" currency={currency} />
                </>
            );
        }

        return null;
    };

    return (
        <>
            <PoolRow sx={{ px: 1.5, py: 2 }}>
                <AccessControl user={user} allowedRoles={[UserRole.ShopUser, UserRole.ShopAdmin]}>
                    <BestPriceFilter />
                </AccessControl>
                <EcoProductFilter />
                <SpecialPriceFilter isSpecialOffer={isSpecialOffer} />
            </PoolRow>

            <Divider />

            <PoolAccordion activeIndex={isPriceOpen ? 0 : undefined}>
                <PoolAccordionItem>
                    <FilterAccordionSummary filterActive={!!minPrice || !!maxPrice}>
                        <Stack>
                            <PoolTypography variant="button" translationId="app.filter.price.label" />
                            <PoolLineClampTypography maxLines={2} variant="caption" color="grey.800">
                                {renderMinMaxPrice()}
                            </PoolLineClampTypography>
                        </Stack>
                    </FilterAccordionSummary>
                    <FilterAccordionDetails>
                        <FilterPrice />
                    </FilterAccordionDetails>
                </PoolAccordionItem>

                <PoolAccordionItem>
                    <FilterAccordionSummary filterActive={!!searchParams.get(FilterParams.SupplierIds)}>
                        <Stack>
                            <PoolTypography variant="button" translationId="app.filter.supplier.label" />
                            <PoolLineClampTypography maxLines={2} variant="caption" color="grey.800">
                                {suppliers[0]
                                    ?.split(',')
                                    .map(
                                        supplierId =>
                                            initialArticleSearchResults.suppliersFromSearch.find(
                                                s => s.id === +supplierId
                                            )?.name
                                    )
                                    .join(', ')}
                            </PoolLineClampTypography>
                        </Stack>
                    </FilterAccordionSummary>
                    <FilterAccordionDetails>
                        <FilterSupplierSearchPage />
                    </FilterAccordionDetails>
                </PoolAccordionItem>

                {Object.keys(initialArticleSearchResults.attributesFromSearch ?? {}).map((key, _) => {
                    const initialAttributes = initialArticleSearchResults.attributesFromSearch?.[key] ?? [];
                    const attributes = articleSearchResults.attributesFromSearch?.[key] ?? [];
                    if (initialAttributes.length === 0) {
                        return null;
                    }

                    const checkedAttributes = initialAttributes.filter(a =>
                        (searchParams.get(FilterParams.AttributeKeys)?.split(',') ?? []).some(attr => attr === a.key)
                    );

                    const filterActive = searchParams
                        .get(FilterParams.AttributeKeys)
                        ?.split(',')
                        .some(attribute => attribute.split('_')[0] === key.split('_')[0]);

                    return (
                        <PoolAccordionItem key={key}>
                            <FilterAccordionSummary filterActive={filterActive}>
                                <Stack>
                                    <PoolTypography variant="button">{initialAttributes[0].name}</PoolTypography>
                                    <PoolLineClampTypography maxLines={2} variant="caption" color="grey.800">
                                        {checkedAttributes.map(a => a.value).join(', ')}
                                    </PoolLineClampTypography>
                                </Stack>
                            </FilterAccordionSummary>
                            <FilterAccordionDetails>
                                {initialAttributes.map(initialAttribute => (
                                    <FilterAttribute
                                        checkedAttributes={checkedAttributes}
                                        attributesFromSearch={attributes}
                                        initialAttribute={initialAttribute}
                                        key={initialAttribute.id}
                                    />
                                ))}
                            </FilterAccordionDetails>
                        </PoolAccordionItem>
                    );
                })}
            </PoolAccordion>
        </>
    );
};
